import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Tab,
  Nav,
  Form,
  Button,
  Table,
  Modal,
  Spinner,
  Badge,
  Image,
  Alert,
  Pagination,
} from "react-bootstrap";
import Card from "../../../components/Card";
import {
  createEquipmentCategory,
  updateEquipmentCategory,
  deleteEquipmentCategory,
  getEquipmentCategories,
  getManufacturers,
  createManufacturer,
  updateManufacturer,
  deleteManufacturer,
  getVariants,
  createVariant,
  updateVariant,
  deleteVariant,
} from "../../../services/service";
import { toast } from "react-toastify";

const EquipmentManager = () => {
  /* -------------------------------------------------
   *  STATE
   * ------------------------------------------------- */
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [ManufacturerDeleteModal, setManufacturerDeleteModal] = useState(false);
  const [deleteCategoryId, setDeleteCategoryId] = useState(null);
  const [deleteCategoryName, setDeleteCategoryName] = useState("");
  const [deleteManufacturerData, setDeleteManufacture] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingManufacturerId, setEditingManufacturerId] = useState(null);
  const [editingVariantId, setEditingVariantId] = useState(null);
  const [modalError, setModalError] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [expandedCategoryId, setExpandedCategoryId] = useState(null);
  const [expandedManufacturerId, setExpandedManufacturerId] = useState(null);
  const [expandedVariantId, setExpandedVariantId] = useState(null);

  // Pagination
  const [categoryPage, setCategoryPage] = useState(1);
  const [manufacturerPage, setManufacturerPage] = useState(1);
  const [variantPage, setVariantPage] = useState(1);
  const [totalCategoryPages, setTotalCategoryPages] = useState(1);
  const [totalManufacturerPages, setTotalManufacturerPages] = useState(1);
  const [totalVariantPages, setTotalVariantPages] = useState(1);
  const recordsPerPage = 10;
  const [activeTab, setActiveTab] = useState("categories");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // ---------- Categories ----------
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({
    name: { en: "", hi: "" },
    description: { en: "", hi: "" },
    image: null,
    previewImage: null,
    rentalType: "",
    rentalTypeHindi: "",
    rentalRate: "",
    deliveryCharge: "",
    acrePerHour: 0,
    feetPerHour: 0,
  });
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [deleteManufacturerId, setDeleteManudacturerId] = useState("");
  // ---------- Manufacturers ----------
  const [manufacturers, setManufacturers] = useState([]);
  const [newManufacturer, setNewManufacturer] = useState({
    name: { en: "", hi: "" },
    country: { en: "", hi: "" },
    description: { en: "", hi: "" },
  });
  const [manufacturerLoading, setManufacturerLoading] = useState(false);

  // ---------- Variants ----------
  const [variants, setVariants] = useState([]);
  const [newVariant, setNewVariant] = useState({
    name: { en: "", hi: "" },
    manufacturerId: "",
    specs: { engine: "", fuelType: "", transmission: "" },
  });
  const [variantLoading, setVariantLoading] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState("");

  // ---------- Modals ----------
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showManufacturerModal, setShowManufacturerModal] = useState(false);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [deleteVariantId, setDeleteVariantId] = useState("");
  const [deleteVariantModal, setDeleteVariantModal] = useState(false);
  const [VariantName, setVariantName] = useState("");
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  /* -------------------------------------------------
   *  EFFECTS – fetch data
   * ------------------------------------------------- */
  useEffect(() => {
    if (activeTab === "categories") {
      fetchCategories(categoryPage);
    } else if (activeTab === "manufacturers") {
      fetchManufacturers(manufacturerPage);
    } else if (activeTab === "variants") {
      fetchVariants(selectedManufacturer || null, variantPage);
    }
  }, [
    activeTab,
    categoryPage,
    manufacturerPage,
    variantPage,
    selectedManufacturer,
    startDate,
    endDate,
    searchTerm,
  ]);
  /* -------------------------------------------------
   *  API CALLS
   * ------------------------------------------------- */
  const fetchCategories = async (page = 1) => {
    setCategoryLoading(true);
    try {
      const res = await getEquipmentCategories({
        page,
        limit: recordsPerPage,
        startDate,
        endDate,
        search: searchTerm,
      });
      const data = res.data?.data || res.data || res;
      const pagination = res.data?.pagination || res.pagination;

      setCategories(Array.isArray(data) ? data : []);
      if (pagination) {
        setTotalCategoryPages(pagination.totalPages || 1);
      }
      setError("");
    } catch (e) {
      setError("Failed to load categories");
      console.error(e);
    } finally {
      setCategoryLoading(false);
    }
  };

  const fetchManufacturers = async (page = 1) => {
    setManufacturerLoading(true);
    try {
      const res = await getManufacturers({
        page,
        limit: recordsPerPage,
        startDate,
        endDate,
        search: searchTerm,
      });
      const data = res.data?.data || res.data || res;
      const pagination = res.data?.pagination || res.pagination;

      setManufacturers(Array.isArray(data) ? data : []);
      if (pagination) {
        setTotalManufacturerPages(pagination.totalPages || 1);
      }
      setError("");
    } catch (e) {
      setError("Failed to load manufacturers");
      console.error(e);
    } finally {
      setManufacturerLoading(false);
    }
  };

  const fetchVariants = async (manufacturerId = null, page = 1) => {
    setVariantLoading(true);
    try {
      const res = await getVariants({
        manufacturerId,
        page,
        limit: recordsPerPage,
        startDate,
        endDate,
        search: searchTerm,
      });
      const data = res.data?.data || res.data || res;
      const pagination = res.data?.pagination || res.pagination;

      setVariants(Array.isArray(data) ? data : []);
      if (pagination) {
        setTotalVariantPages(pagination.totalPages || 1);
      }
      setError("");
    } catch (e) {
      setError("Failed to load variants");
      console.error(e);
    } finally {
      setVariantLoading(false);
    }
  };

  const downloadCategoryCSV = async () => {
    try {
      setDownloading(true);
      const res = await getEquipmentCategories({
        page: 1,
        limit: 10000,
        startDate,
        endDate,
      });
      const data = res.data?.data || res.data || res;
      if (Array.isArray(data)) {
        const csvRows = [
          [
            "ID",
            "Name",
            "Speed",
            "RentalRate",
            "DeliveryCharge",
            "CreatedAt",
          ],
        ];

        data.forEach((cat) => {
          let speed = "-";
          if (cat.rentalType === "area" && cat.acrePerHour) {
            speed = `${cat.acrePerHour} acre/hr`;
          } else if (cat.rentalType === "ft" && cat.feetPerHour) {
            speed = `${cat.feetPerHour} ft/hr`;
          }

          const nameEn = cat.name?.en || "";
          const nameHi = cat.name?.hi || "";
          const combinedName =
            nameEn && nameHi
              ? `${nameEn} (${nameHi})`
              : nameEn || nameHi || "-";

          const row = [
            `"${cat._id}"`,
            `"${combinedName.replace(/"/g, '""')}"`,
            `"${speed}"`,
            `"${cat.rentalRate || 0}"`,
            `"${cat.deliveryCharge || 0}"`,
            `"${new Date(cat.createdAt).toLocaleString("en-IN", {
              timeZone: "Asia/Kolkata",
            })}"`,
          ];
          csvRows.push(row.join(","));
        });

        const csvString = csvRows.join("\n");
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `equipment_categories_${new Date().getTime()}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (e) {
      toast.error("Failed to download categories CSV");
    } finally {
      setDownloading(false);
    }
  };

  const downloadManufacturerCSV = async () => {
    try {
      setDownloading(true);
      const res = await getManufacturers({
        page: 1,
        limit: 10000,
        startDate,
        endDate,
      });
      const data = res.data?.data || res.data || res;
      if (Array.isArray(data)) {
        const csvRows = [
          ["ID", "Name", "Country", "Description", "CreatedAt"],
        ];
        data.forEach((m) => {
          const nameEn = m.name?.en || "";
          const nameHi = m.name?.hi || "";
          const combinedName =
            nameEn && nameHi ? `${nameEn} (${nameHi})` : nameEn || nameHi || "-";

          const countryEn = m.country?.en || "";
          const countryHi = m.country?.hi || "";
          const combinedCountry =
            countryEn && countryHi
              ? `${countryEn} (${countryHi})`
              : countryEn || countryHi || "-";

          const descEn = m.description?.en || "";
          const descHi = m.description?.hi || "";
          const combinedDesc =
            descEn && descHi ? `${descEn} (${descHi})` : descEn || descHi || "-";

          csvRows.push([
            `"${m._id}"`,
            `"${combinedName.replace(/"/g, '""')}"`,
            `"${combinedCountry.replace(/"/g, '""')}"`,
            `"${combinedDesc.replace(/"/g, '""')}"`,
            `"${new Date(m.createdAt).toLocaleString("en-IN", {
              timeZone: "Asia/Kolkata",
            })}"`,
          ]);
        });
        const csvString = csvRows.map((row) => row.join(",")).join("\n");
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `manufacturers_${new Date().getTime()}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (e) {
      toast.error("Failed to download manufacturers CSV");
    } finally {
      setDownloading(false);
    }
  };

  const downloadVariantCSV = async () => {
    try {
      setDownloading(true);
      const res = await getVariants({
        manufacturerId: selectedManufacturer || null,
        page: 1,
        limit: 10000,
        startDate,
        endDate,
      });
      const data = res.data?.data || res.data || res;
      if (Array.isArray(data)) {
        const csvRows = [
          [
            "ID",
            "Name",
            "Manufacturer",
            "Engine",
            "FuelType",
            "Transmission",
            "CreatedAt",
          ],
        ];
        data.forEach((v) => {
          const nameEn = v.name?.en || "";
          const nameHi = v.name?.hi || "";
          const combinedName =
            nameEn && nameHi ? `${nameEn} (${nameHi})` : nameEn || nameHi || "-";

          const manufacturerName = v.manufacturer?.name?.en || "-";

          csvRows.push([
            `"${v._id}"`,
            `"${combinedName.replace(/"/g, '""')}"`,
            `"${manufacturerName.replace(/"/g, '""')}"`,
            `"${(v.specs?.engine || "-").replace(/"/g, '""')}"`,
            `"${(v.specs?.fuelType || "-").replace(/"/g, '""')}"`,
            `"${(v.specs?.transmission || "-").replace(/"/g, '""')}"`,
            `"${new Date(v.createdAt).toLocaleString("en-IN", {
              timeZone: "Asia/Kolkata",
            })}"`,
          ]);
        });
        const csvString = csvRows.map((row) => row.join(",")).join("\n");
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `equipment_variants_${new Date().getTime()}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (e) {
      toast.error("Failed to download variants CSV");
    } finally {
      setDownloading(false);
    }
  };

  /* -------------------------------------------------
   *  CATEGORY HANDLERS
   * ------------------------------------------------- */
  const handleEditCategory = (cat) => {
    setEditingCategoryId(cat._id);

    // Normalize nested fields
    const safeName =
      typeof cat.name === "object" ? cat.name : { en: cat.name || "", hi: "" };
    const safeDescription =
      typeof cat.description === "object"
        ? cat.description
        : { en: cat.description || "", hi: "" };

    setNewCategory({
      name: safeName,
      description: safeDescription,
      image: null,
      previewImage: cat.image || null,
      rentalType: cat.rentalType || "",
      rentalTypeHindi: cat.rentalTypeHindi || "",
      rentalRate: cat.rentalRate || "",
      deliveryCharge: cat.deliveryCharge ?? "",
      acrePerHour: cat.acrePerHour ?? 0,
      feetPerHour: cat.feetPerHour ?? 0,
    });

    setShowCategoryModal(true);
  };

  const handleDeleteCategory = (cat) => {
    setDeleteError("");
    setDeleteCategoryId(cat._id);
    setDeleteCategoryName(cat.name?.en || "");
    setShowDeleteModal(true);
  };

  const confirmDeleteCategory = async () => {
    setCategoryLoading(true);
    try {
      const res = await deleteEquipmentCategory(deleteCategoryId);
      if (res.success) {
        await fetchCategories();
        setShowDeleteModal(false);
        setDeleteCategoryId(null);
        setDeleteCategoryName("");
        toast.success("Category deleted successfully!");
      } else {
        setDeleteError(res.message || "Cannot delete this category.");
        toast.error("Cannot delete this category.");
      }
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to delete category";
      setDeleteError(msg);
      toast.error(msg);
    } finally {
      setCategoryLoading(false);
      setShowDeleteModal(false);
      setDeleteError("");
    }
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    setCategoryLoading(true);
    setModalError("");

    try {
      const form = new FormData();
      form.append("name.en", newCategory.name.en);
      form.append("name.hi", newCategory.name.hi);
      form.append("description.en", newCategory.description.en);
      form.append("description.hi", newCategory.description.hi);
      form.append("rentalType", newCategory.rentalType);
      form.append("rentalTypeHindi", newCategory.rentalTypeHindi);
      form.append("rentalRate", newCategory.rentalRate);
      form.append("deliveryCharge", newCategory.deliveryCharge);

      if (newCategory.image) form.append("image", newCategory.image);
      if (newCategory.rentalType === "area") {
        form.append("acrePerHour", newCategory.acrePerHour);
      }
      if (newCategory.rentalType === "ft") {
        form.append("feetPerHour", newCategory.feetPerHour);
      }
      if (editingCategoryId) {
        await updateEquipmentCategory(editingCategoryId, form);
        toast.success("Category updated successfully!");
      } else {
        if (!newCategory.image) {
          setModalError("Please select an image");
          toast.error("Please upload an image before saving.");
          setCategoryLoading(false);
          return;
        }
        await createEquipmentCategory(form);
        toast.success("Category created successfully!");
      }

      await fetchCategories();
      closeCategoryModal();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to save category";
      setModalError(msg);
      toast.error(msg);
    } finally {
      setCategoryLoading(false);
    }
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setEditingCategoryId(null);
    setModalError("");
    setNewCategory({
      name: { en: "", hi: "" },
      description: { en: "", hi: "" },
      image: null,
      previewImage: null,
      rentalType: "",
      rentalTypeHindi: "",
      rentalRate: "",
      deliveryCharge: "",
      acrePerHour: 0,
      feetPerHour: 0,
    });
  };

  /* -------------------------------------------------
   *  MANUFACTURER HANDLERS
   * ------------------------------------------------- */
  const handleEditManufacturer = (man) => {
    setEditingManufacturerId(man._id);
    setNewManufacturer({
      name: man.name || { en: "", hi: "" },
      country: man.country || { en: "", hi: "" },
      description: man.description || { en: "", hi: "" },
    });
    setShowManufacturerModal(true);
  };

  const handleDeleteManufacturer = async (man) => {
    setDeleteManudacturerId(man?._id);
    setManufacturerDeleteModal(true);
    setDeleteManufacture(man?.name?.en);
  };
  const confirmDeleteManufacture = async () => {
    setManufacturerLoading(true);
    try {
      const res = await deleteManufacturer(deleteManufacturerId);
      await fetchManufacturers();
      setManufacturerDeleteModal(false);
      setManufacturerDeleteModal(null);
      setDeleteManufacture("");
      toast.success("Manufacture deleted successfully!");
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to delete Manufacture";
      toast.error(msg);
    } finally {
      setManufacturerLoading(false);
      setManufacturerDeleteModal(false);
    }
  };
  const handleSaveManufacturer = async (e) => {
    e.preventDefault();
    setManufacturerLoading(true);
    try {
      const payload = {
        name: newManufacturer.name,
        country: newManufacturer.country,
        description: newManufacturer.description,
      };

      if (editingManufacturerId) {
        await updateManufacturer(editingManufacturerId, payload);
        toast.success("Manufacturer updated successfully!");
      } else {
        await createManufacturer(payload);
        toast.success("Manufacturer created successfully!");
      }

      await fetchManufacturers();
      setShowManufacturerModal(false);
      setEditingManufacturerId(null);
      setNewManufacturer({
        name: { en: "", hi: "" },
        country: { en: "", hi: "" },
        description: { en: "", hi: "" },
      });
    } catch (e) {
      setError("Failed to save manufacturer");
      toast.error("Failed to save manufacturer");
      console.error(e);
    } finally {
      setManufacturerLoading(false);
    }
  };
  const handleConfirmDeleteVariant = async (man) => {
    setDeleteVariantId(man?._id);
    setDeleteVariantModal(true);
    setVariantName(man?.name?.en);
  };
  /* -------------------------------------------------
   *  VARIANT HANDLERS
   * ------------------------------------------------- */
  const handleEditVariant = (varnt) => {
    setEditingVariantId(varnt._id);
    setNewVariant({
      manufacturerId: varnt.manufacturer?._id || varnt.manufacturer || "",
      name: varnt.name || { en: "", hi: "" },
      specs: varnt.specs || { engine: "", fuelType: "", transmission: "" },
    });
    setShowVariantModal(true);
  };

  const handleDeleteVariant = async () => {
    // if (!window.confirm("Delete this variant?")) return;
    setVariantLoading(true);
    try {
      await deleteVariant(deleteVariantId);
      await fetchVariants(selectedManufacturer || null);
      toast.success("Variant deleted successfully!");
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to delete Manufacture";
      toast.error(msg);
      console.error(e);
    } finally {
      setVariantLoading(false);
      setDeleteVariantModal(false);
    }
  };

  const handleSaveVariant = async (e) => {
    e.preventDefault();
    setVariantLoading(true);
    setError("");

    try {
      const manufacturer = newVariant.manufacturerId?.trim();
      if (!manufacturer) {
        setError("Please select a Manufacturer");
        setVariantLoading(false);
        return;
      }
      if (!newVariant.name.en?.trim()) {
        setError("Variant name (English) is required");
        setVariantLoading(false);
        return;
      }

      const payload = {
        manufacturer,
        name: {
          en: newVariant.name.en.trim(),
          hi: newVariant.name.hi?.trim() || "",
        },
        specs: newVariant.specs,
      };

      if (editingVariantId) {
        await updateVariant(editingVariantId, payload);
        toast.success("Variant updated successfully!");
      } else {
        await createVariant(payload);
        toast.success("Variant added successfully!");
      }

      await fetchVariants(selectedManufacturer || null);

      setShowVariantModal(false);
      setEditingVariantId(null);
      setNewVariant({
        name: { en: "", hi: "" },
        manufacturerId: "",
        specs: { engine: "", fuelType: "", transmission: "" },
      });
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to save variant";
      setError(msg);
      console.error(err);
    } finally {
      setVariantLoading(false);
    }
  };

  /* -------------------------------------------------
   *  UI HELPERS
   * ------------------------------------------------- */
  const paginate = (arr, page) => {
    const start = (page - 1) * recordsPerPage;
    return arr.slice(start, start + recordsPerPage);
  };

  const totalPages = (arr) => Math.ceil(arr.length / recordsPerPage);

  /* -------------------------------------------------
   *  SAFE RENDER HELPERS
   * ------------------------------------------------- */
  const renderLang = (obj) => {
    if (!obj) return "-";
    const parts = [];
    if (obj.en) parts.push(obj.en);
    if (obj.hi) parts.push(obj.hi);
    return parts.length ? parts.join(" / ") : "-";
  };

  const renderName = (name) => {
    if (!name) return "-";
    if (typeof name === "string") return name;
    return renderLang(name);
  };

  /* -------------------------------------------------
   *  DESCRIPTION EXPAND LOGIC
   * ------------------------------------------------- */
  const DescriptionCell = ({ item, type }) => {
    const fullDesc = [
      typeof item.description === "string" ? item.description : "",
      item.description?.en || "",
      item.description?.hi || "",
    ]
      .filter(Boolean)
      .join(" / ")
      .trim();

    if (!fullDesc) return <>-</>;

    const words = fullDesc.split(/\s+/);
    const isExpanded =
      expandedCategoryId === item._id ||
      expandedManufacturerId === item._id ||
      expandedVariantId === item._id;
    const shortDesc =
      words.length > 60 ? words.slice(0, 60).join(" ") + "..." : fullDesc;

    const toggleExpand = (e) => {
      e.stopPropagation();
      if (type === "category")
        setExpandedCategoryId(isExpanded ? null : item._id);
      else if (type === "manufacturer")
        setExpandedManufacturerId(isExpanded ? null : item._id);
      else if (type === "variant")
        setExpandedVariantId(isExpanded ? null : item._id);
    };

    return (
      <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
        {isExpanded ? fullDesc : shortDesc}{" "}
        {words.length > 60 && (
          <button
            type="button"
            onClick={toggleExpand}
            style={{
              background: "none",
              border: "none",
              color: "#007bff",
              cursor: "pointer",
              display: "inline",
              padding: 0,
              marginLeft: "4px",
              textDecoration: "underline",
              fontSize: "0.875rem",
            }}
          >
            {isExpanded ? "Read less" : "Read more"}
          </button>
        )}
      </div>
    );
  };

  /* -------------------------------------------------
   *  RENDER
   * ------------------------------------------------- */
  return (
    <div>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Equipment Management</h4>
            </Card.Header>

            <Card.Body className="p-0">
              {error && (
                <Alert variant="danger" className="m-3">
                  {error}
                </Alert>
              )}

              <div className="p-3 border-bottom bg-light">
                <Row className="align-items-center">
                  <Col md={3}>
                    <Form.Group className="mb-0">
                      <Form.Label className="small mb-1">Start Date</Form.Label>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-0">
                      <Form.Label className="small mb-1">End Date</Form.Label>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-0">
                      <Form.Label className="small mb-1">Search</Form.Label>
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Search keyword..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3} className="text-end pt-4">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setStartDate("");
                        setEndDate("");
                        setSearchTerm("");
                      }}
                    >
                      Clear Filters
                    </Button>
                  </Col>
                </Row>
              </div>

              <Tab.Container
                activeKey={activeTab}
                onSelect={(k) => {
                  setActiveTab(k);
                  setSearchTerm("");
                }}
              >
                <Row className="m-0">
                  <Col sm={12} className="p-0">
                    <Nav variant="tabs" className="px-3 pt-3">
                      <Nav.Item>
                        <Nav.Link eventKey="categories" className="px-4">
                          Categories
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="manufacturers" className="px-4">
                          Manufacturers
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="variants" className="px-4">
                          Variants
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </Col>

                  <Col sm={12}>
                    <Tab.Content className="p-3">
                      {/* ==================== CATEGORIES ==================== */}
                      <Tab.Pane eventKey="categories">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <h5 className="mb-0">Equipment Categories</h5>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={downloadCategoryCSV}
                              disabled={downloading}
                            >
                              {downloading ? "Downloading..." : "Download CSV"}
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => setShowCategoryModal(true)}
                            >
                              Add Category
                            </Button>
                          </div>
                        </div>

                        <div className="table-responsive">
                          <Table striped hover className="text-nowrap">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Image</th>
                                <th>Description</th>
                                <th>Speed</th>
                                <th>Rental Rate</th>
                                <th>Delivery Charge</th>
                                <th>Created</th>
                                <th className="text-end">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {categoryLoading ? (
                                <tr>
                                  <td colSpan={7} className="text-center">
                                    <Spinner
                                      animation="border"
                                      size="sm"
                                      className="me-2"
                                    />
                                    Loading...
                                  </td>
                                </tr>
                              ) : categories.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={7}
                                    className="text-center py-4 text-muted"
                                  >
                                    No categories found{" "}
                                    <Button
                                      variant="link"
                                      onClick={() => setShowCategoryModal(true)}
                                    >
                                      Add first
                                    </Button>
                                  </td>
                                </tr>
                              ) : (
                                categories.map((c) => (
                                  <tr key={c._id}>
                                    <td>
                                      <div>
                                        <strong>{renderName(c.name)}</strong>
                                      </div>
                                    </td>
                                    <td>
                                      {c.image ? (
                                        <Image
                                          src={c.image}
                                          thumbnail
                                          width={50}
                                        />
                                      ) : (
                                        "-"
                                      )}
                                    </td>
                                    <td style={{ minWidth: "350px" }}>
                                      <DescriptionCell
                                        item={c}
                                        type="category"
                                      />
                                    </td>
                                    <td>
                                      {c.rentalType === "area" &&
                                        c.acrePerHour ? (
                                        <Badge bg="primary">
                                          {c.acrePerHour} acre/hr
                                        </Badge>
                                      ) : c.rentalType === "ft" &&
                                        c.feetPerHour ? (
                                        <Badge bg="warning">
                                          {c.feetPerHour} ft/hr
                                        </Badge>
                                      ) : (
                                        <Badge bg="secondary">-</Badge>
                                      )}
                                    </td>
                                    <td>
                                      <Badge bg="info">
                                        ₹{c.rentalRate}/
                                        {c.rentalType === "hour"
                                          ? "hr"
                                          : c.rentalType === "area"
                                            ? "acre"
                                            : c.rentalType === "ft"
                                              ? "feet"
                                              : "-"}
                                      </Badge>
                                      {c.rentalTypeHindi && (
                                        <div className="text-muted small">
                                          {c.rentalTypeHindi}
                                        </div>
                                      )}
                                    </td>

                                    <td>
                                      {c.deliveryCharge > 0 ? (
                                        <Badge bg="success">
                                          ₹{c.deliveryCharge}
                                        </Badge>
                                      ) : (
                                        <Badge bg="secondary">Free</Badge>
                                      )}
                                    </td>
                                    <td>
                                      {new Date(
                                        c.createdAt
                                      ).toLocaleDateString()}
                                    </td>
                                    <td className="text-end">
                                      <Button
                                        variant="success"
                                        size="sm"
                                        className="me-2"
                                        onClick={() => handleEditCategory(c)}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() => handleDeleteCategory(c)}
                                      >
                                        Delete
                                      </Button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </Table>
                        </div>

                        {totalCategoryPages > 1 && (
                          <div className="d-flex justify-content-center mt-3">
                            <Pagination>
                              <Pagination.First
                                onClick={() => setCategoryPage(1)}
                                disabled={categoryPage === 1}
                              />
                              <Pagination.Prev
                                disabled={categoryPage === 1}
                                onClick={() =>
                                  setCategoryPage((p) => Math.max(p - 1, 1))
                                }
                              />

                              {/* Show first page if not in window */}
                              {categoryPage > 3 && (
                                <>
                                  <Pagination.Item
                                    onClick={() => setCategoryPage(1)}
                                  >
                                    1
                                  </Pagination.Item>
                                  {categoryPage > 4 && (
                                    <Pagination.Ellipsis disabled />
                                  )}
                                </>
                              )}

                              {/* Window of pages around current page */}
                              {[...Array(totalCategoryPages)].map((_, i) => {
                                const page = i + 1;
                                if (
                                  page >= categoryPage - 2 &&
                                  page <= categoryPage + 2
                                ) {
                                  return (
                                    <Pagination.Item
                                      key={page}
                                      active={page === categoryPage}
                                      onClick={() => setCategoryPage(page)}
                                    >
                                      {page}
                                    </Pagination.Item>
                                  );
                                }
                                return null;
                              })}

                              {/* Show last page if not in window */}
                              {categoryPage < totalCategoryPages - 2 && (
                                <>
                                  {categoryPage < totalCategoryPages - 3 && (
                                    <Pagination.Ellipsis disabled />
                                  )}
                                  <Pagination.Item
                                    onClick={() =>
                                      setCategoryPage(totalCategoryPages)
                                    }
                                  >
                                    {totalCategoryPages}
                                  </Pagination.Item>
                                </>
                              )}

                              <Pagination.Next
                                disabled={categoryPage === totalCategoryPages}
                                onClick={() =>
                                  setCategoryPage((p) =>
                                    Math.min(p + 1, totalCategoryPages)
                                  )
                                }
                              />
                              <Pagination.Last
                                onClick={() =>
                                  setCategoryPage(totalCategoryPages)
                                }
                                disabled={categoryPage === totalCategoryPages}
                              />
                            </Pagination>
                          </div>
                        )}
                      </Tab.Pane>

                      {/* ==================== MANUFACTURERS ==================== */}
                      <Tab.Pane eventKey="manufacturers">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <h5 className="mb-0">Equipment Manufacturers</h5>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={downloadManufacturerCSV}
                              disabled={downloading}
                            >
                              {downloading ? "Downloading..." : "Download CSV"}
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => setShowManufacturerModal(true)}
                            >
                              Add Manufacturer
                            </Button>
                          </div>
                        </div>

                        <div className="table-responsive">
                          <Table striped hover className="text-nowrap">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Country</th>
                                <th>Description</th>
                                <th className="text-end">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {manufacturerLoading ? (
                                <tr>
                                  <td colSpan={4} className="text-center">
                                    <Spinner
                                      animation="border"
                                      size="sm"
                                      className="me-2"
                                    />
                                    Loading...
                                  </td>
                                </tr>
                              ) : manufacturers.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={4}
                                    className="text-center py-4 text-muted"
                                  >
                                    No manufacturers{" "}
                                    <Button
                                      variant="link"
                                      onClick={() =>
                                        setShowManufacturerModal(true)
                                      }
                                    >
                                      Add first
                                    </Button>
                                  </td>
                                </tr>
                              ) : (
                                manufacturers.map((m) => (
                                  <tr key={m._id}>
                                    <td className="fw-medium">
                                      <div>{renderName(m.name)}</div>
                                    </td>
                                    <td>
                                      <div>{renderName(m.country)}</div>
                                    </td>
                                    <td style={{ minWidth: "300px" }}>
                                      <DescriptionCell
                                        item={m}
                                        type="manufacturer"
                                      />
                                    </td>
                                    <td className="text-end">
                                      <Button
                                        variant="success"
                                        size="sm"
                                        className="me-2"
                                        onClick={() =>
                                          handleEditManufacturer(m)
                                        }
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() =>
                                          handleDeleteManufacturer(m)
                                        }
                                      >
                                        Delete
                                      </Button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </Table>
                        </div>

                        {totalManufacturerPages > 1 && (
                          <div className="d-flex justify-content-center mt-3">
                            <Pagination>
                              <Pagination.First
                                onClick={() => setManufacturerPage(1)}
                                disabled={manufacturerPage === 1}
                              />
                              <Pagination.Prev
                                disabled={manufacturerPage === 1}
                                onClick={() =>
                                  setManufacturerPage((p) =>
                                    Math.max(p - 1, 1)
                                  )
                                }
                              />

                              {/* Show first page if not in window */}
                              {manufacturerPage > 3 && (
                                <>
                                  <Pagination.Item
                                    onClick={() => setManufacturerPage(1)}
                                  >
                                    1
                                  </Pagination.Item>
                                  {manufacturerPage > 4 && (
                                    <Pagination.Ellipsis disabled />
                                  )}
                                </>
                              )}

                              {/* Window of pages around current page */}
                              {[...Array(totalManufacturerPages)].map(
                                (_, i) => {
                                  const page = i + 1;
                                  if (
                                    page >= manufacturerPage - 2 &&
                                    page <= manufacturerPage + 2
                                  ) {
                                    return (
                                      <Pagination.Item
                                        key={page}
                                        active={page === manufacturerPage}
                                        onClick={() =>
                                          setManufacturerPage(page)
                                        }
                                      >
                                        {page}
                                      </Pagination.Item>
                                    );
                                  }
                                  return null;
                                }
                              )}

                              {/* Show last page if not in window */}
                              {manufacturerPage <
                                totalManufacturerPages - 2 && (
                                  <>
                                    {manufacturerPage <
                                      totalManufacturerPages - 3 && (
                                        <Pagination.Ellipsis disabled />
                                      )}
                                    <Pagination.Item
                                      onClick={() =>
                                        setManufacturerPage(totalManufacturerPages)
                                      }
                                    >
                                      {totalManufacturerPages}
                                    </Pagination.Item>
                                  </>
                                )}

                              <Pagination.Next
                                disabled={
                                  manufacturerPage === totalManufacturerPages
                                }
                                onClick={() =>
                                  setManufacturerPage((p) =>
                                    Math.min(p + 1, totalManufacturerPages)
                                  )
                                }
                              />
                              <Pagination.Last
                                onClick={() =>
                                  setManufacturerPage(totalManufacturerPages)
                                }
                                disabled={
                                  manufacturerPage === totalManufacturerPages
                                }
                              />
                            </Pagination>
                          </div>
                        )}
                      </Tab.Pane>

                      {/* ==================== VARIANTS ==================== */}
                      <Tab.Pane eventKey="variants">
                        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
                          <h5 className="mb-0">Equipment Variants</h5>
                          <div className="d-flex align-items-center gap-2">
                            <Form.Group
                              className="mb-0"
                              style={{ minWidth: "200px" }}
                            >
                              <Form.Select
                                size="sm"
                                value={selectedManufacturer}
                                onChange={(e) =>
                                  setSelectedManufacturer(e.target.value)
                                }
                              >
                                <option value="">All Manufacturers</option>
                                {manufacturers.map((m) => (
                                  <option key={m._id} value={m._id}>
                                    {renderName(m.name) || "-"}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={downloadVariantCSV}
                              disabled={downloading}
                            >
                              {downloading ? "Downloading..." : "Download CSV"}
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => setShowVariantModal(true)}
                            >
                              Add Variant
                            </Button>
                          </div>
                        </div>

                        <div className="table-responsive">
                          <Table striped hover className="text-nowrap">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Manufacturer</th>
                                <th>Engine</th>
                                <th>Fuel Type</th>
                                <th>Transmission</th>
                                <th className="text-end">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {variantLoading ? (
                                <tr>
                                  <td colSpan={6} className="text-center">
                                    <Spinner
                                      animation="border"
                                      size="sm"
                                      className="me-2"
                                    />
                                    Loading...
                                  </td>
                                </tr>
                              ) : variants.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={6}
                                    className="text-center py-4 text-muted"
                                  >
                                    No variants
                                  </td>
                                </tr>
                              ) : (
                                variants.map((v) => (
                                  <tr key={v._id}>
                                    <td>
                                      <div>{renderName(v.name)}</div>
                                    </td>
                                    <td>
                                      <div>
                                        {renderName(v.manufacturer?.name)}
                                      </div>
                                    </td>
                                    <td>{v.specs?.engine || "-"}</td>
                                    <td>{v.specs?.fuelType || "-"}</td>
                                    <td>{v.specs?.transmission || "-"}</td>
                                    <td className="text-end">
                                      <Button
                                        variant="success"
                                        size="sm"
                                        className="me-2"
                                        onClick={() => handleEditVariant(v)}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() =>
                                          handleConfirmDeleteVariant(v)
                                        }
                                      >
                                        Delete
                                      </Button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </Table>
                        </div>

                        {totalVariantPages > 1 && (
                          <div className="d-flex justify-content-center mt-3">
                            <Pagination>
                              <Pagination.First
                                onClick={() => setVariantPage(1)}
                                disabled={variantPage === 1}
                              />
                              <Pagination.Prev
                                disabled={variantPage === 1}
                                onClick={() =>
                                  setVariantPage((p) => Math.max(p - 1, 1))
                                }
                              />

                              {/* Show first page if not in window */}
                              {variantPage > 3 && (
                                <>
                                  <Pagination.Item
                                    onClick={() => setVariantPage(1)}
                                  >
                                    1
                                  </Pagination.Item>
                                  {variantPage > 4 && (
                                    <Pagination.Ellipsis disabled />
                                  )}
                                </>
                              )}

                              {/* Window of pages around current page */}
                              {[...Array(totalVariantPages)].map((_, i) => {
                                const page = i + 1;
                                if (
                                  page >= variantPage - 2 &&
                                  page <= variantPage + 2
                                ) {
                                  return (
                                    <Pagination.Item
                                      key={page}
                                      active={page === variantPage}
                                      onClick={() => setVariantPage(page)}
                                    >
                                      {page}
                                    </Pagination.Item>
                                  );
                                }
                                return null;
                              })}

                              {/* Show last page if not in window */}
                              {variantPage < totalVariantPages - 2 && (
                                <>
                                  {variantPage < totalVariantPages - 3 && (
                                    <Pagination.Ellipsis disabled />
                                  )}
                                  <Pagination.Item
                                    onClick={() =>
                                      setVariantPage(totalVariantPages)
                                    }
                                  >
                                    {totalVariantPages}
                                  </Pagination.Item>
                                </>
                              )}

                              <Pagination.Next
                                disabled={variantPage === totalVariantPages}
                                onClick={() =>
                                  setVariantPage((p) =>
                                    Math.min(p + 1, totalVariantPages)
                                  )
                                }
                              />
                              <Pagination.Last
                                onClick={() =>
                                  setVariantPage(totalVariantPages)
                                }
                                disabled={variantPage === totalVariantPages}
                              />
                            </Pagination>
                          </div>
                        )}
                      </Tab.Pane>
                    </Tab.Content>
                  </Col>
                </Row>
              </Tab.Container>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ==================== CATEGORY MODAL ==================== */}
      <Modal show={showCategoryModal} onHide={closeCategoryModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCategoryId ? "Edit Category" : "Add New Category"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && <Alert variant="danger">{modalError}</Alert>}
          <Form onSubmit={handleSaveCategory}>
            <Form.Group className="mb-3">
              <Form.Label>Category Name (English) *</Form.Label>
              <Form.Control
                type="text"
                value={newCategory.name.en}
                onChange={(e) =>
                  setNewCategory({
                    ...newCategory,
                    name: { ...newCategory.name, en: e.target.value },
                  })
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>श्रेणी का नाम (Hindi)</Form.Label>
              <Form.Control
                type="text"
                value={newCategory.name.hi}
                onChange={(e) =>
                  setNewCategory({
                    ...newCategory,
                    name: { ...newCategory.name, hi: e.target.value },
                  })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description (English)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={newCategory.description.en}
                onChange={(e) =>
                  setNewCategory({
                    ...newCategory,
                    description: {
                      ...newCategory.description,
                      en: e.target.value,
                    },
                  })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>विवरण (Hindi)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={newCategory.description.hi}
                onChange={(e) =>
                  setNewCategory({
                    ...newCategory,
                    description: {
                      ...newCategory.description,
                      hi: e.target.value,
                    },
                  })
                }
              />
            </Form.Group>
            {/* ENGLISH → HINDI AUTO-FILL + DISABLE */}
            <Form.Group className="mb-3">
              <Form.Label>Rental Type *</Form.Label>
              <Form.Select
                value={newCategory.rentalType}
                onChange={(e) => {
                  const selected = e.target.value;
                  const hindiMap = {
                    hour: "घंटे के आधार पर",
                    area: "क्षेत्र के आधार पर",
                    ft: "फुट",
                  };
                  setNewCategory({
                    ...newCategory,
                    rentalType: selected,
                    rentalTypeHindi: hindiMap[selected] || "",
                  });
                }}
                required
              >
                <option value="">Select...</option>
                <option value="hour">Hourly</option>
                <option value="area">Area-based</option>
                <option value="ft">Feet</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Rental Type (Hindi)</Form.Label>
              <Form.Select value={newCategory.rentalTypeHindi} disabled>
                <option value="">चुनें...</option>
                <option value="घंटे के आधार पर">घंटे के आधार पर</option>
                <option value="क्षेत्र के आधार पर">क्षेत्र के आधार पर</option>
                <option value="फुट">फुट</option>
              </Form.Select>
            </Form.Group>
            {newCategory.rentalType === "area" && (
              <Form.Group className="mb-3">
                <Form.Label>Acre Per Hour *</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={newCategory.acrePerHour}
                  onChange={(e) =>
                    setNewCategory({
                      ...newCategory,
                      acrePerHour: e.target.value,
                    })
                  }
                  required
                />
              </Form.Group>
            )}

            {/* ------------------- FEET FIELD ------------------- */}
            {newCategory.rentalType === "ft" && (
              <Form.Group className="mb-3">
                <Form.Label>Feet Per Hour *</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={newCategory.feetPerHour}
                  onChange={(e) =>
                    setNewCategory({
                      ...newCategory,
                      feetPerHour: e.target.value,
                    })
                  }
                  required
                />
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Rental Rate *</Form.Label>
              <Form.Control
                type="number"
                min="0"
                placeholder={
                  newCategory.rentalType === "hour"
                    ? "Per hour"
                    : newCategory.rentalType === "area"
                      ? "Per acre"
                      : newCategory.rentalType === "ft"
                        ? "Per feet"
                        : "Enter rate"
                }
                value={newCategory.rentalRate}
                onChange={(e) =>
                  setNewCategory({ ...newCategory, rentalRate: e.target.value })
                }
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Delivery Charge (₹)(Per Km)</Form.Label>
              <Form.Control
                type="number"
                step="1"
                value={newCategory.deliveryCharge}
                onChange={(e) =>
                  setNewCategory({
                    ...newCategory,
                    deliveryCharge: Number(e.target.value),
                  })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Image {editingCategoryId ? "" : "*"}</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setNewCategory({
                    ...newCategory,
                    image: file,
                    previewImage: file
                      ? URL.createObjectURL(file)
                      : newCategory.previewImage,
                  });
                }}
                required={!editingCategoryId}
              />
              {newCategory.previewImage && (
                <div className="mt-2 text-center">
                  <Image
                    src={newCategory.previewImage}
                    thumbnail
                    style={{ maxHeight: "120px" }}
                  />
                </div>
              )}
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={closeCategoryModal}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={categoryLoading}
              >
                {categoryLoading ? (
                  <>Saving...</>
                ) : editingCategoryId ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ==================== DELETE CONFIRM ==================== */}
      <Modal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setDeleteError("");
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Delete <strong>{deleteCategoryName}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteError("");
            }}
          >
            Cancel
          </Button>
          {!deleteError && (
            <Button
              variant="danger"
              onClick={confirmDeleteCategory}
              disabled={categoryLoading}
            >
              {categoryLoading ? "Deleting..." : "Delete"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      {/* ==================== DELETE CONFIRM Manufacturers ==================== */}
      <Modal
        show={ManufacturerDeleteModal}
        onHide={() => {
          setManufacturerDeleteModal(false);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Manufacturers</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Delete <strong>{deleteManufacturerData}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setManufacturerDeleteModal(false);
            }}
          >
            Cancel
          </Button>
          {!deleteError && (
            <Button
              variant="danger"
              onClick={confirmDeleteManufacture}
              disabled={manufacturerLoading}
            >
              {manufacturerLoading ? "Deleting..." : "Delete"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      {/* ==================== DELETE CONFIRM Variant ==================== */}
      <Modal
        show={deleteVariantModal}
        onHide={() => {
          setDeleteVariantModal(false);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete variant</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteError ? (
            <Alert variant="danger">{deleteError}</Alert>
          ) : (
            <p>
              Delete <strong>{VariantName}</strong>?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setDeleteVariantModal(false);
            }}
          >
            Cancel
          </Button>
          {!deleteError && (
            <Button
              variant="danger"
              onClick={handleDeleteVariant}
              disabled={variantLoading}
            >
              {variantLoading ? "Deleting..." : "Delete"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* ==================== MANUFACTURER MODAL ==================== */}
      <Modal
        show={showManufacturerModal}
        onHide={() => {
          setShowManufacturerModal(false);
          setEditingManufacturerId(null);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingManufacturerId ? "Edit" : "Add"} Manufacturer
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSaveManufacturer}>
            <Form.Group className="mb-3">
              <Form.Label>Name (English) *</Form.Label>
              <Form.Control
                type="text"
                value={newManufacturer.name.en}
                onChange={(e) =>
                  setNewManufacturer({
                    ...newManufacturer,
                    name: { ...newManufacturer.name, en: e.target.value },
                  })
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              deleteVariantModal<Form.Label>नाम (Hindi)</Form.Label>
              <Form.Control
                type="text"
                value={newManufacturer.name.hi}
                onChange={(e) =>
                  setNewManufacturer({
                    ...newManufacturer,
                    name: { ...newManufacturer.name, hi: e.target.value },
                  })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Country (English)</Form.Label>
              deleteVariantModal
              <Form.Control
                type="text"
                value={newManufacturer.country.en}
                onChange={(e) =>
                  setNewManufacturer({
                    ...newManufacturer,
                    country: { ...newManufacturer.country, en: e.target.value },
                  })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>देश (Hindi)</Form.Label>
              <Form.Control
                type="text"
                value={newManufacturer.country.hi}
                onChange={(e) =>
                  setNewManufacturer({
                    ...newManufacturer,
                    country: { ...newManufacturer.country, hi: e.target.value },
                  })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description (English)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={newManufacturer.description.en}
                onChange={(e) =>
                  setNewManufacturer({
                    ...newManufacturer,
                    description: {
                      ...newManufacturer.description,
                      en: e.target.value,
                    },
                  })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>विवरण (Hindi)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={newManufacturer.description.hi}
                onChange={(e) =>
                  setNewManufacturer({
                    ...newManufacturer,
                    description: {
                      ...newManufacturer.description,
                      hi: e.target.value,
                    },
                  })
                }
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setShowManufacturerModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={manufacturerLoading}
              >
                {manufacturerLoading
                  ? "Saving..."
                  : editingManufacturerId
                    ? "Update"
                    : "Create"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ==================== VARIANT MODAL ==================== */}
      <Modal
        show={showVariantModal}
        onHide={() => {
          setShowVariantModal(false);
          setEditingVariantId(null);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{editingVariantId ? "Edit" : "Add"} Variant</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSaveVariant}>
            <Form.Group className="mb-3">
              <Form.Label>Manufacturer *</Form.Label>
              <Form.Select
                value={newVariant.manufacturerId}
                onChange={(e) =>
                  setNewVariant({
                    ...newVariant,
                    manufacturerId: e.target.value,
                  })
                }
                required
              >
                <option value="">-- Select --</option>
                {manufacturers.map((m) => (
                  <option key={m._id} value={m._id}>
                    {renderName(m.name) || "-"}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Variant Name (English) *</Form.Label>
              <Form.Control
                type="text"
                value={newVariant.name.en}
                onChange={(e) =>
                  setNewVariant({
                    ...newVariant,
                    name: { ...newVariant.name, en: e.target.value },
                  })
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>वैरिएंट नाम (Hindi)</Form.Label>
              <Form.Control
                type="text"
                value={newVariant.name.hi}
                onChange={(e) =>
                  setNewVariant({
                    ...newVariant,
                    name: { ...newVariant.name, hi: e.target.value },
                  })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Engine</Form.Label>
              <Form.Control
                type="text"
                value={newVariant.specs.engine}
                onChange={(e) =>
                  setNewVariant({
                    ...newVariant,
                    specs: { ...newVariant.specs, engine: e.target.value },
                  })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Fuel Type</Form.Label>
              <Form.Control
                type="text"
                value={newVariant.specs.fuelType}
                onChange={(e) =>
                  setNewVariant({
                    ...newVariant,
                    specs: { ...newVariant.specs, fuelType: e.target.value },
                  })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Transmission</Form.Label>
              <Form.Control
                type="text"
                value={newVariant.specs.transmission}
                onChange={(e) =>
                  setNewVariant({
                    ...newVariant,
                    specs: {
                      ...newVariant.specs,
                      transmission: e.target.value,
                    },
                  })
                }
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setShowVariantModal(false)}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={variantLoading}>
                {variantLoading
                  ? "Saving..."
                  : editingVariantId
                    ? "Update"
                    : "Create"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default EquipmentManager;
